import React, { useState } from "react";
import {
  HiUsers,
  HiBuildingOffice,
  HiChartBar,
  HiChatBubbleLeftRight,
  HiBars3,
  HiRectangleGroup,
  HiMegaphone,
  HiQuestionMarkCircle,
  HiDocumentChartBar,
  HiClipboard,
  HiChartPie,
  HiCubeTransparent,
  HiArrowPath,
} from "react-icons/hi2";
import FeedbackManagement from "./FeedbackManagement";
import UserManagement from "../../components/UserManagement";
import UserAnalytics from "./UserAnalytics";
import CampaignManagement from "./CampaignManagement";
import WellnessQuestionManagement from "./WellnessQuestionManagement";
import SourceMaterialManagement from "./SourceMaterialManagement";
import CoachesCornerManagement from "./CoachesCornerManagement";
import SuggestedAthletesManagement from "./SuggestedAthletesManagement";
import GlobalActivitiesManagement from "./GlobalActivitiesManagement";
import { useIndexedUserData } from "../../hooks/useIndexedUserData";

const AdminPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { stats, loading, refreshData } = useIndexedUserData();

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: <HiRectangleGroup /> },
    { id: "user-analytics", label: "User Analytics", icon: <HiChartPie /> },
    { id: "user-management", label: "User Management", icon: <HiUsers /> },
    {
      id: "suggested-athletes",
      label: "Suggested Athletes",
      icon: <HiUsers />,
    },
    {
      id: "wellness-questions",
      label: "Wellness Questions",
      icon: <HiQuestionMarkCircle />,
    },
    {
      id: "source-material",
      label: "Peak Source Material",
      icon: <HiDocumentChartBar />,
    },
    {
      id: "global-activities",
      label: "Default Library",
      icon: <HiCubeTransparent />,
    },
    { id: "campaigns", label: "Campaigns", icon: <HiMegaphone /> },
    { id: "crm", label: "CRM", icon: <HiBuildingOffice /> },
    { id: "analytics", label: "Analytics", icon: <HiChartBar /> },
    { id: "feedback", label: "Feedback", icon: <HiChatBubbleLeftRight /> },
    { id: "coaches-corner", label: "Coaches Corner", icon: <HiClipboard /> },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const renderContent = () => {
    switch (selectedTab) {
      case "dashboard":
        const chartData = [
          {
            label: "Wellness Logs",
            value: stats.totalDayPosts,
            color: "bg-emerald-500",
          },
          {
            label: "Training Logs",
            value: stats.totalTrainingLogs,
            color: "bg-blue-500",
          },
          {
            label: "Activity Logs",
            value: stats.totalActivityLogs,
            color: "bg-orange-500",
          },
          {
            label: "Sessions",
            value: stats.totalSessions,
            color: "bg-cyan-500",
          },
          {
            label: "AI Chats",
            value: stats.totalAiChats,
            color: "bg-purple-500",
          },
          {
            label: "Social Posts",
            value: stats.totalPosts,
            color: "bg-pink-500",
          },
        ];
        const maxValue = Math.max(...chartData.map((d) => d.value), 1);

        return (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-semibold">Admin Dashboard</h1>
              <button
                onClick={refreshData}
                disabled={loading}
                className="btn btn-primary btn-sm"
              >
                <HiArrowPath
                  className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                Refresh Data
              </button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="card bg-base-100 shadow-sm border border-base-200">
                <div className="card-body p-4">
                  <div className="text-3xl font-bold text-primary">
                    {stats.totalUsers.toLocaleString()}
                  </div>
                  <div className="text-sm text-base-content/70">Total Users</div>
                </div>
              </div>
              <div className="card bg-base-100 shadow-sm border border-base-200">
                <div className="card-body p-4">
                  <div className="text-3xl font-bold text-warning">
                    {stats.platinumUsers + stats.premiumUsers}
                  </div>
                  <div className="text-sm text-base-content/70">Paid Users</div>
                </div>
              </div>
              <div className="card bg-base-100 shadow-sm border border-base-200">
                <div className="card-body p-4">
                  <div className="text-3xl font-bold text-success">
                    {stats.usersWithWearables}
                  </div>
                  <div className="text-sm text-base-content/70">With Wearables</div>
                </div>
              </div>
              <div className="card bg-base-100 shadow-sm border border-base-200">
                <div className="card-body p-4">
                  <div className="text-3xl font-bold text-info">
                    {stats.avgDayPostsPerUser}
                  </div>
                  <div className="text-sm text-base-content/70">Avg Wellness/User</div>
                </div>
              </div>
            </div>

            {/* Activity Chart */}
            <div className="card bg-base-100 shadow-sm border border-base-200 mb-8">
              <div className="card-body">
                <h2 className="text-xl font-semibold mb-6">
                  Platform Activity Overview
                </h2>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <span className="loading loading-spinner loading-lg"></span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chartData.map((item) => (
                      <div key={item.label} className="flex items-center gap-4">
                        <div className="w-32 text-sm text-base-content/70 text-right">
                          {item.label}
                        </div>
                        <div className="flex-1 bg-base-200 rounded-full h-8 overflow-hidden">
                          <div
                            className={`h-full ${item.color} rounded-full transition-all duration-500 flex items-center justify-end pr-3`}
                            style={{
                              width: `${Math.max((item.value / maxValue) * 100, 2)}%`,
                            }}
                          >
                            {item.value > 0 && (
                              <span className="text-white text-sm font-semibold">
                                {item.value.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        {item.value === 0 && (
                          <span className="text-base-content/50 text-sm">0</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Subscription Breakdown */}
            <div className="card bg-base-100 shadow-sm border border-base-200">
              <div className="card-body">
                <h2 className="text-xl font-semibold mb-6">
                  Subscription Breakdown
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-gradient-to-br from-amber-500/10 to-amber-500/15 rounded-lg">
                    <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                      {stats.platinumUsers}
                    </div>
                    <div className="text-sm text-base-content/70">Platinum</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-green-500/10 to-green-500/15 rounded-lg">
                    <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                      {stats.premiumUsers}
                    </div>
                    <div className="text-sm text-base-content/70">Premium</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-blue-500/10 to-blue-500/15 rounded-lg">
                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                      {stats.coachUsers}
                    </div>
                    <div className="text-sm text-base-content/70">Coach</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-purple-500/10 to-purple-500/15 rounded-lg">
                    <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                      {stats.plusUsers}
                    </div>
                    <div className="text-sm text-base-content/70">Plus</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-base-200 to-base-300 rounded-lg">
                    <div className="text-2xl font-bold text-base-content/80">
                      {stats.defaultUsers}
                    </div>
                    <div className="text-sm text-base-content/70">Free</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "user-analytics":
        return <UserAnalytics />;
      case "user-management":
        return <UserManagement />;
      case "suggested-athletes":
        return <SuggestedAthletesManagement />;
      case "wellness-questions":
        return <WellnessQuestionManagement />;
      case "source-material":
        return <SourceMaterialManagement />;
      case "global-activities":
        return <GlobalActivitiesManagement />;
      case "campaigns":
        return <CampaignManagement />;
      case "crm":
        return (
          <div>
            <h1 className="text-3xl font-semibold mb-6">CRM</h1>
            <p className="text-base-content/70">Coming soon...</p>
          </div>
        );
      case "analytics":
        return (
          <div>
            <h1 className="text-3xl font-semibold mb-6">Analytics</h1>
            <p className="text-base-content/70">Coming soon...</p>
          </div>
        );
      case "feedback":
        return <FeedbackManagement />;
      case "coaches-corner":
        return <CoachesCornerManagement />;
      default:
        return null;
    }
  };

  const sidebarContent = (
    <div>
      <div className="p-6 border-b border-base-300">
        <div className="flex items-center font-bold text-xl">
          <h1 className="text-3xl font-groteskLogo">
            PEAK<span className="font-light text-base-content/60">^</span>
          </h1>
        </div>
        <h2 className="text-lg font-semibold text-primary">Admin Panel</h2>
      </div>
      <nav className="pt-4">
        {menuItems.map((item) => (
          <div key={item.id} className="px-4 mb-2">
            <button
              onClick={() => {
                setSelectedTab(item.id);
                if (window.innerWidth < 768) {
                  setMobileOpen(false);
                }
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-grotesk ${
                selectedTab === item.id
                  ? "bg-base-300 text-base-content"
                  : "text-base-content hover:bg-base-200 hover:text-base-content"
              }`}
            >
              <div className="w-5 h-5">{item.icon}</div>
              <span
                className={`font-medium ${
                  selectedTab === item.id ? "font-semibold" : ""
                }`}
              >
                {item.label}
              </span>
            </button>
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-base-100 shadow-sm border-b border-base-300">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={handleDrawerToggle}
            className="p-2 hover:bg-base-200 rounded-md transition-colors"
          >
            <HiBars3 className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold">Admin Panel</h1>
          <div className="w-9"></div> {/* Spacer for centering */}
        </div>
      </div>

      {/* Sidebar */}
      <nav className="w-70 flex-shrink-0 hidden md:block">
        <div className="fixed top-0 left-0 w-70 h-full bg-base-100 border-r border-base-300">
          {sidebarContent}
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={handleDrawerToggle}
          ></div>
          <div className="fixed top-0 left-0 w-70 h-full bg-base-100">
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-70">
        <div className="p-6 mt-16 md:mt-0">
          <div className="bg-base-100 rounded-lg shadow-sm border border-base-200 p-8 min-h-[calc(100vh-120px)]">
            {renderContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
